import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from "react";

interface HolyModeContextValue {
  enabled: boolean;
  toggle: () => void;
}

const HolyModeContext = createContext<HolyModeContextValue | null>(null);
const STORAGE_PREFIX = "vammo.holy-mode.v1:";

function storageKey(userId: string | null): string | null {
  return userId ? `${STORAGE_PREFIX}${encodeURIComponent(userId)}` : null;
}

function readPreference(userId: string | null): boolean {
  const key = storageKey(userId);
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === "on";
  } catch {
    return false;
  }
}

function writePreference(userId: string | null, enabled: boolean): void {
  const key = storageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, enabled ? "on" : "off");
  } catch {
    // Keep the live preference in memory when browser storage is unavailable.
  }
}

export function HolyModeProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const instanceKey = userId === null ? "anonymous" : `user:${userId}`;
  return <UserHolyModeProvider key={instanceKey} userId={userId}>{children}</UserHolyModeProvider>;
}

function UserHolyModeProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const [enabled, setEnabled] = useState(() => readPreference(userId));
  const toggle = useCallback(() => {
    const next = !enabled;
    setEnabled(next);
    writePreference(userId, next);
  }, [enabled, userId]);

  useLayoutEffect(() => {
    document.documentElement.dataset.holyMode = enabled ? "on" : "off";
    const favicon = document.getElementById("app-favicon");
    if (favicon instanceof HTMLLinkElement) favicon.href = enabled ? "/holy-favicon.svg" : "/favicon.svg";
  }, [enabled]);

  useLayoutEffect(() => () => {
    document.documentElement.dataset.holyMode = "off";
    const favicon = document.getElementById("app-favicon");
    if (favicon instanceof HTMLLinkElement) favicon.href = "/favicon.svg";
  }, []);

  const value = useMemo(() => ({ enabled, toggle }), [enabled, toggle]);
  return <HolyModeContext.Provider value={value}>{children}</HolyModeContext.Provider>;
}

export function useHolyMode(): HolyModeContextValue {
  const context = useContext(HolyModeContext);
  if (!context) throw new Error("useHolyMode must be used within HolyModeProvider");
  return context;
}

export function useHolyModeOptional(): HolyModeContextValue {
  return useContext(HolyModeContext) ?? { enabled: false, toggle: () => {} };
}
